import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Target, 
  Shield, 
  Zap, 
  Trophy,
  CheckCircle,
  ArrowRight,
  Star,
  TrendingUp,
  Users,
  PlayCircle,
  DollarSign,
  HelpCircle,
  Mail,
  Phone,
  MapPin,
  Minus,
  Plus
} from 'lucide-react';

const features = [
  {
    icon: Target,
    title: 'Taxa Fixa',
    description: 'Apenas R$ 10 por rifa criada. Sem comissões percentuais!'
  },
  {
    icon: Shield,
    title: 'Segurança Total',
    description: 'Sorteios transparentes e auditáveis com tecnologia blockchain'
  },
  {
    icon: Zap,
    title: 'Rápido e Fácil',
    description: 'Crie sua rifa em menos de 2 minutos'
  },
  {
    icon: Trophy,
    title: 'Múltiplos Formatos',
    description: 'Números, nomes, animais ou personalizadas'
  }
];

const stats = [
  { label: 'Rifas Criadas', value: '10,000+' },
  { label: 'Usuários Ativos', value: '5,000+' },
  { label: 'Total Arrecadado', value: 'R$ 2M+' },
  { label: 'Taxa de Satisfação', value: '98%' }
];

const howItWorksSteps = [
  {
    step: '01',
    icon: Users,
    title: 'Cadastre-se',
    description: 'Crie sua conta gratuita em menos de 1 minuto'
  },
  {
    step: '02',
    icon: Target,
    title: 'Crie sua Rifa',
    description: 'Configure prêmios, números e valores da sua rifa'
  },
  {
    step: '03',
    icon: PlayCircle,
    title: 'Compartilhe',
    description: 'Divulgue sua rifa nas redes sociais e WhatsApp'
  },
  {
    step: '04',
    icon: Trophy,
    title: 'Realize o Sorteio',
    description: 'Sorteio automático e transparente na data escolhida'
  }
];

const pricingPlans = [
  {
    name: 'Participante',
    price: 'Gratuito',
    period: 'sempre',
    description: 'Para quem quer participar de rifas',
    features: [
      'Participar de rifas ilimitadas',
      'Acompanhar resultados',
      'Notificações por email',
      'Histórico de participações'
    ],
    buttonText: 'Começar Grátis',
    popular: false
  },
  {
    name: 'Criador',
    price: 'R$ 29',
    period: '/mês',
    description: 'Para criar rifas ocasionalmente',
    features: [
      'Até 5 rifas por mês',
      'Suporte por email',
      'Relatórios básicos',
      'Integração WhatsApp',
      'Sorteio automático',
      'Taxa fixa por rifa: R$ 10'
    ],
    buttonText: 'Assinar Criador',
    popular: true
  },
  {
    name: 'Profissional',
    price: 'R$ 99',
    period: '/mês',
    description: 'Para organizadores profissionais',
    features: [
      'Rifas ilimitadas',
      'Suporte prioritário 24/7',
      'Relatórios avançados',
      'API personalizada',
      'Múltiplos administradores',
      'White-label disponível',
      'Taxa reduzida: R$ 5 por rifa'
    ],
    buttonText: 'Assinar Profissional',
    popular: false
  }
];

const faqData = [
  {
    question: 'Como funciona o sistema de pagamento?',
    answer: 'Os participantes pagam diretamente para você via PIX, cartão ou transferência. A Rifa Tudo não retém o dinheiro, apenas cobra uma taxa fixa por rifa criada.'
  },
  {
    question: 'O sorteio é realmente transparente?',
    answer: 'Sim! Utilizamos tecnologia blockchain para garantir que o sorteio seja completamente aleatório e auditável por qualquer pessoa.'
  },
  {
    question: 'Posso criar rifas de qualquer valor?',
    answer: 'Sim, você define o valor dos números da sua rifa. Não há limite mínimo ou máximo estabelecido pela plataforma.'
  },
  {
    question: 'Como divulgo minha rifa?',
    answer: 'Fornecemos ferramentas de compartilhamento para redes sociais, WhatsApp e um link personalizado para sua rifa.'
  },
  {
    question: 'E se eu precisar cancelar uma rifa?',
    answer: 'Você pode cancelar rifas que ainda não foram sorteadas. Os participantes são notificados automaticamente e podem solicitar reembolso.'
  },
  {
    question: 'Existe suporte técnico?',
    answer: 'Sim! Oferecemos suporte por email para todos os planos, e suporte 24/7 para o plano Profissional.'
  }
];

// Navigation component
const NavigationMenu = () => {
  const [activeSection, setActiveSection] = useState('hero');

  const sections = [
    { id: 'hero', label: 'Início' },
    { id: 'como-funciona', label: 'Como Funciona' },
    { id: 'precos', label: 'Preços' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contato', label: 'Contato' }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'como-funciona', 'precos', 'faq', 'contato'];
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed top-1/2 right-8 transform -translate-y-1/2 z-50 hidden lg:block">
      <nav className="flex flex-col space-y-2">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => scrollToSection(section.id)}
            className={`group relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
              activeSection === section.id
                ? 'bg-primary text-white'
                : 'bg-white/80 text-muted-foreground hover:bg-primary hover:text-white'
            }`}
          >
            <span className="relative z-10">{section.label}</span>
            {activeSection !== section.id && (
              <div className="absolute inset-0 bg-primary opacity-0 group-hover:opacity-100 transition-opacity rounded-full" />
            )}
          </button>
        ))}
      </nav>
    </div>
  );
};

// FAQ Item component
const FAQItem = ({ question, answer, isOpen, onToggle }: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  return (
    <div className="border border-border rounded-lg">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <span className="font-semibold text-lg">{question}</span>
        {isOpen ? (
          <Minus className="h-5 w-5 text-primary" />
        ) : (
          <Plus className="h-5 w-5 text-primary" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-4">
          <p className="text-muted-foreground leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

export function ModernHomePage() {
  const { user } = useAuth();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <Layout>
      <NavigationMenu />
      
      {/* Hero Section */}
      <section id="hero" className="relative overflow-hidden bg-gradient-to-br from-primary/20 via-background to-purple-500/20">
        <div className="container px-4 py-24 mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center space-x-2 mb-6">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">Avaliado por 1000+ usuários</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold tracking-tight mb-6">
                Crie Rifas{' '}
                <span className="bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                  Incríveis
                </span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                A plataforma mais moderna do Brasil para criar e gerenciar rifas online. 
                Taxa fixa, pagamento direto e total transparência.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                {user ? (
                  <>
                    <Button asChild size="lg" className="text-lg px-8">
                      <Link to="/create-raffle">
                        Criar Minha Rifa
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="text-lg px-8">
                      <Link to="/dashboard">
                        Ir ao Dashboard
                      </Link>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button asChild size="lg" className="text-lg px-8">
                      <Link to="/auth/register">
                        Começar Agora
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="text-lg px-8">
                      <Link to="/auth/login">
                        Fazer Login
                      </Link>
                    </Button>
                  </>
                )}
              </div>

              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Sem taxa de setup</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Suporte 24/7</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-purple-500/20 blur-3xl" />
              <Card className="relative backdrop-blur-sm bg-card/80">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Target className="h-6 w-6 text-primary" />
                    <span>Rifa do iPhone 15 Pro</span>
                  </CardTitle>
                  <CardDescription>
                    128GB - Titânio Natural
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-10 gap-1">
                      {[...Array(50)].map((_, i) => (
                        <div
                          key={i}
                          className={`aspect-square rounded text-xs flex items-center justify-center font-semibold ${
                            i < 23 
                              ? 'bg-green-500 text-white' 
                              : i < 30 
                                ? 'bg-yellow-500 text-white'
                                : 'bg-muted text-muted-foreground hover:bg-primary hover:text-white cursor-pointer transition-colors'
                          }`}
                        >
                          {i + 1}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Vendidos: 23/100</span>
                      <span className="font-semibold">R$ 10,00 cada</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full w-[23%]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-muted/30">
        <div className="container px-4 mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="container px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Por que escolher a Rifa Tudo?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              A plataforma mais completa e confiável para suas rifas online
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <feature.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="como-funciona" className="py-24 bg-muted/30">
        <div className="container px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Como Funciona?
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Em apenas 4 passos simples você pode criar e gerenciar suas rifas online
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <step.icon className="h-8 w-8 text-primary" />
                  </div>
                  <div className="absolute -top-2 -right-2 bg-primary text-white text-sm font-bold rounded-full w-8 h-8 flex items-center justify-center">
                    {step.step}
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="py-24">
        <div className="container px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Planos para Todos
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Escolha o plano ideal para suas necessidades
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative ${plan.popular ? 'scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Mais Popular
                    </span>
                  </div>
                )}
                <Card className={`h-full ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
                  <CardHeader className="text-center">
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <CardDescription className="text-base">{plan.description}</CardDescription>
                    <div className="mt-6">
                      <div className="flex items-baseline justify-center">
                        <span className="text-4xl font-bold">{plan.price}</span>
                        <span className="text-muted-foreground ml-1">{plan.period}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    <ul className="space-y-3 mb-8 flex-1">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className={`w-full ${plan.popular ? 'bg-primary' : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                      asChild
                    >
                      <Link to="/auth/register">
                        {plan.buttonText}
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-muted/30">
        <div className="container px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Tire suas dúvidas sobre a plataforma
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <FAQItem
                    question={faq.question}
                    answer={faq.answer}
                    isOpen={openFAQ === index}
                    onToggle={() => toggleFAQ(index)}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-24">
        <div className="container px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Entre em Contato
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Estamos aqui para ajudar você com suas rifas
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <HelpCircle className="h-6 w-6 text-primary" />
                    <span>Informações de Contato</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <Mail className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-semibold">Email</p>
                      <p className="text-muted-foreground">contato@rifatudo.com</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Phone className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-semibold">WhatsApp</p>
                      <p className="text-muted-foreground">(11) 99999-9999</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <MapPin className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-semibold">Endereço</p>
                      <p className="text-muted-foreground">São Paulo, SP - Brasil</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="h-full">
                <CardHeader>
                  <CardTitle>Envie sua Mensagem</CardTitle>
                  <CardDescription>
                    Responderemos em até 24 horas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Nome</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Seu nome completo"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email</label>
                      <input
                        type="email"
                        className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="seu@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Mensagem</label>
                      <textarea
                        rows={4}
                        className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Como podemos ajudar?"
                      />
                    </div>
                    <Button className="w-full">
                      <Mail className="mr-2 h-4 w-4" />
                      Enviar Mensagem
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary to-purple-600">
        <div className="container px-4 mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Pronto para começar?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Crie sua primeira rifa em menos de 2 minutos. 
              Taxa fixa de apenas R$ 10 por rifa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="text-lg px-8">
                <Link to="/auth/register">
                  <TrendingUp className="mr-2 h-5 w-5" />
                  Criar Conta Grátis
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/20 text-white hover:bg-white hover:text-primary">
                <Link to="/auth/login">
                  Já tenho conta
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}